import { __rest } from "tslib";
import React, { useCallback, useMemo, useEffect, useRef, useState, useLayoutEffect, } from 'react';
import { Menu } from '@sas/nova-commons/components/Menu';
import { ReportContextMenuState, } from '../../store/reportContextMenu/ReportContextMenuState';
import { useI18n } from '../../locale/I18nContext';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useGetCustomMenuItems } from '../ReportContainer/MenuItemProvider';
import { BirdState } from '../../store/bird/birdState';
function toContextMenuItem(localize, item) {
    return {
        text: localize(item.text),
        subSections: item.subSections && toContextMenu(localize, item.subSections),
        checked: item.checked,
        disabled: item.disabled,
        icon: item.icon,
        // Set testId regardless of exposeTestId() to support Pendo feature tagging in VA
        testId: item.testId,
    };
}
function toContextMenu(localize, sections) {
    return sections.map((section) => {
        const items = section.items.map((item) => toContextMenuItem(localize, item));
        return { items };
    });
}
export const ReportContextMenuBase = (_a) => {
    var _b, _c, _d, _e;
    var { actions, birdActions, dispatch } = _a, state = __rest(_a, ["actions", "birdActions", "dispatch"]);
    const localize = useI18n();
    const [customItems, setCustomItems] = useState({ report: [], object: [] });
    // Update custom items when state.target changes. getCustomMenuItems does not change.
    const getCustomMenuItems = useGetCustomMenuItems();
    useEffect(() => {
        var _a;
        setCustomItems({
            report: getCustomMenuItems({ type: 'report' }),
            object: ((_a = state.target) === null || _a === void 0 ? void 0 : _a.id) ? getCustomMenuItems({ type: 'object', name: state.target.id }) : [],
        });
    }, [state.target, getCustomMenuItems]);
    const reportItems = (_c = (_b = state.reportItemSortKeys) === null || _b === void 0 ? void 0 : _b.map((key) => state.reportItems[key]).filter((item) => !!item)) !== null && _c !== void 0 ? _c : [];
    const objectItems = (_e = (_d = state.objectItemSortKeys) === null || _d === void 0 ? void 0 : _d.map((key) => (key ? state.objectItems[key] : {})).filter((item) => !!item)) !== null && _e !== void 0 ? _e : [];
    let menuSections;
    {
        const allItems = [
            ...reportItems,
            ...customItems.report,
            { type: 'separator' },
            ...objectItems,
            ...customItems.object,
        ];
        menuSections = [{ items: [] }];
        allItems.forEach((item) => {
            if (item.type === 'separator') {
                menuSections.push({ items: [] });
            }
            else {
                menuSections[menuSections.length - 1].items.push(item);
            }
        });
        // Remove empty sections
        menuSections = menuSections.filter((section) => section.items.length > 0);
    }
    const onItemSelected = useCallback((indexPairs) => {
        if (!indexPairs.length)
            return;
        const [[section, index], ...rest] = indexPairs;
        let item = menuSections[section].items[index];
        for (const [section, index] of rest) {
            if (!item.subSections)
                return; // The item could not be found
            item = item.subSections[section].items[index];
        }
        if (item.action) {
            dispatch(item.action);
            actions.close();
        }
        if (item.callback) {
            item.callback();
            actions.close();
        }
    }, [actions, menuSections, dispatch]);
    const contextMenuSections = useMemo(() => toContextMenu(localize, menuSections), [
        menuSections,
        localize,
    ]);
    const isContextMenuOpen = state.open && menuSections.length > 0;
    const lastFocusedElement = useRef(null);
    useLayoutEffect(() => {
        if (isContextMenuOpen) {
            // Context menu is opening; remember which element was focused last
            lastFocusedElement.current = document.activeElement;
        }
    }, [isContextMenuOpen]);
    function restoreFocus() {
        var _a;
        (_a = lastFocusedElement.current) === null || _a === void 0 ? void 0 : _a.focus();
        lastFocusedElement.current = null;
    }
    return (React.createElement(Menu, { isOpen: isContextMenuOpen, position: state.position, sections: isContextMenuOpen ? contextMenuSections : [], onItemSelected: (event) => {
            // Context menu is closing; set focus on the last-focused element. Restore focus immediately
            //   so focus does not get stolen from the new section when returning from a page link.
            restoreFocus();
            onItemSelected(event);
        }, onDismiss: () => {
            restoreFocus();
            actions.close();
        } }));
};
export const ReportContextMenu = connect((state) => state.ui.reportContextMenu, (dispatch) => ({
    actions: bindActionCreators(ReportContextMenuState.actions, dispatch),
    birdActions: bindActionCreators(BirdState.actions, dispatch),
    dispatch,
}))(ReportContextMenuBase);
